import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { writeAccessLicenseStates } from '@pixacare/domain';
import { toClientUser } from '@pixacare/dtos';
import { ClientUserHttpService } from '@services/http/client-user.http.service';
import { DepartmentHttpService } from '@services/http/department.http.service';
import { LicenseHttpService } from '@services/http/license.http.service';
import { departmentsActions } from '@shared/store/departments/departments.actions';
import { lifeCycleActions } from '@shared/store/life-cycle/life-cycle.actions';
import { notificationsActions } from '@shared/store/notifications/notifications.actions';
import { usersActions } from '@shared/store/users/users.actions';
import { catchError, mergeMap, switchMap } from 'rxjs';
import { departmentUsersAdminActions } from './department-users-admin.actions';

@Injectable()
export class DepartmentUsersAdminEffects {
  private actions$ = inject(Actions);
  private licenseService = inject(LicenseHttpService);
  private clientService = inject(ClientUserHttpService);
  private readonly departmentService = inject(DepartmentHttpService);
  private readonly translocoService = inject(TranslocoService);

  getDepartmentMemberships$ = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentsActions.getDepartmentMemberships),
      mergeMap(({ clientCode, departmentId }) =>
        this.departmentService.getDepartmentUsers(clientCode, departmentId).pipe(
          switchMap((departmentUserResponse) => [
            departmentsActions.getDepartmentMembershipsSuccess({
              clientCode,
              departmentId,
              memberships: departmentUserResponse.map((deptUser) => ({
                ...deptUser.membership,
                hasActiveLicense: writeAccessLicenseStates.includes(deptUser.licenseState),
              })),
            }),
            usersActions.addUsersSuccess({
              clientUsers: departmentUserResponse.map((deptUser) => toClientUser(deptUser)),
            }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  addUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentUsersAdminActions.addUsers),
      mergeMap(({ mailAddresses, clientCode, departmentId }) =>
        this.clientService.addClientUser(clientCode, mailAddresses, departmentId).pipe(
          switchMap((clientUsers) => [
            departmentsActions.addUsersMembershipSuccess({
              clientCode,
              departmentId,
              userIds: clientUsers.map((user) => user.id),
            }),
            usersActions.addUsersSuccess({
              clientUsers,
            }),
            notificationsActions.success({
              title: this.translocoService.translate(
                '{count, plural, =1 {Invitation envoyée} other {Invitations envoyées}}',
                { count: mailAddresses.length },
              ),

              message: this.translocoService.translate(
                // eslint-disable-next-line max-len
                "{count, plural, =1 {L'utilisateur a bien été invité au groupe.} other {Les utilisateurs ont bien été invités au groupe.}}",
                { count: mailAddresses.length },
              ),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,

              title: this.translocoService.translate(
                // eslint-disable-next-line max-len
                "{count, plural, =1 {Impossible d'inviter l'utilisateur au groupe.} other {Impossible d'inviter les utilisateurs au groupe.}}",
                { count: mailAddresses.length },
              ),
              message: this.translocoService.translate(`Un problème est survenu.`),
            }),
          ]),
        ),
      ),
    ),
  );

  deleteInvitedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(departmentUsersAdminActions.deleteInvitedUser),
      mergeMap(({ clientCode, userId, licenseId, departmentIds }) =>
        this.licenseService.removeLicense(licenseId).pipe(
          switchMap(() => [
            usersActions.removeUser({ userId }),
            ...(departmentIds ?? []).map((departmentId: number) =>
              departmentsActions.removeUserMembership({ clientCode, departmentId, userId }),
            ),
            notificationsActions.success({
              title: this.translocoService.translate('Utilisateur supprimé'),
              message: this.translocoService.translate(`L'utilisateur a bien été supprimé du groupe.`),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate("Impossible de supprimer l'utilisateur du groupe."),
              message: this.translocoService.translate(`Un problème est survenu.`),
            }),
          ]),
        ),
      ),
    ),
  );
}
